import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
} from '@goaltree/ui/components/field'
import { Input } from '@goaltree/ui/components/input'
import { useForm } from '@tanstack/react-form'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import z from 'zod'

import { authClient } from '@/lib/auth-client'

export const Route = createFileRoute('/auth/reset-password')({
  head: () => ({
    meta: [{ title: 'Set new password — Goaltree' }],
  }),
  validateSearch: (search) => ({
    token: typeof search.token === 'string' ? search.token : '',
  }),
  component: RouteComponent,
})

function RouteComponent() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { token } = Route.useSearch()

  const form = useForm({
    defaultValues: { password: '', confirm: '' },
    onSubmit: async ({ value }) => {
      if (!token) {
        toast.error(t('auth.resetInvalidBody'))
        return
      }
      await authClient.resetPassword(
        { newPassword: value.password, token },
        {
          onSuccess: () => {
            toast.success(t('auth.resetSuccess'))
            navigate({ to: '/auth/login', replace: true })
          },
          onError: (error) => void toast.error(error.error.message || error.error.statusText),
        },
      )
    },
    validators: {
      onSubmit: z
        .object({
          password: z.string().min(8, t('auth.resetPasswordMin')),
          confirm: z.string(),
        })
        .refine((data) => data.password === data.confirm, {
          message: t('auth.resetPasswordMatch'),
          path: ['confirm'],
        }),
    },
  })

  if (!token) {
    return (
      <div className="flex flex-col gap-6">
        <div className="flex flex-col items-center gap-1 text-center">
          <h1 className="text-xl font-bold">{t('auth.resetInvalidTitle')}</h1>
          <p className="text-sm text-muted-foreground">{t('auth.resetInvalidBody')}</p>
        </div>
        <FieldDescription className="text-center">
          <button
            type="button"
            onClick={() => navigate({ to: '/auth/forgot-password' })}
            className="cursor-pointer font-medium text-foreground underline underline-offset-4 hover:text-primary-text"
          >
            {t('auth.resetRequestNew')}
          </button>
        </FieldDescription>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col items-center gap-1 text-center">
        <h1 className="text-xl font-bold">{t('auth.resetTitle')}</h1>
        <p className="text-sm text-muted-foreground">{t('auth.resetSubtitle')}</p>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault()
          e.stopPropagation()
          form.handleSubmit()
        }}
      >
        <FieldGroup>
          <form.Field name="password">
            {(field) => (
              <Field data-invalid={field.state.meta.errors.length > 0 || undefined}>
                <FieldLabel htmlFor={field.name}>{t('auth.resetNewPassword')}</FieldLabel>
                <Input
                  id={field.name}
                  name={field.name}
                  type="password"
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                  placeholder={t('auth.resetPasswordPlaceholder')}
                  autoFocus
                  className="h-11 rounded-lg px-4 text-sm"
                />
                <FieldError errors={field.state.meta.errors} />
              </Field>
            )}
          </form.Field>

          <form.Field name="confirm">
            {(field) => (
              <Field data-invalid={field.state.meta.errors.length > 0 || undefined}>
                <FieldLabel htmlFor={field.name}>{t('auth.resetConfirmPassword')}</FieldLabel>
                <Input
                  id={field.name}
                  name={field.name}
                  type="password"
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                  placeholder={t('auth.resetConfirmPlaceholder')}
                  className="h-11 rounded-lg px-4 text-sm"
                />
                <FieldError errors={field.state.meta.errors} />
              </Field>
            )}
          </form.Field>

          <form.Subscribe
            selector={(s) => ({ canSubmit: s.canSubmit, isSubmitting: s.isSubmitting })}
          >
            {({ canSubmit, isSubmitting }) => (
              <Field>
                <button
                  type="submit"
                  disabled={!canSubmit || isSubmitting}
                  className="flex h-11 w-full cursor-pointer items-center justify-center gap-2 rounded-full bg-primary text-sm font-bold text-primary-foreground transition-all hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      {t('auth.updating')}
                    </>
                  ) : (
                    t('auth.resetUpdate')
                  )}
                </button>
              </Field>
            )}
          </form.Subscribe>
        </FieldGroup>
      </form>
    </div>
  )
}
